import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { getImageProps, Image, type StaticImageData } from 'Components/Image';
import { Button } from 'Components/LegacyButton';
import { Typography } from 'Components/Typography';

const Section = styled.section`
  width: 100%;

  background-color: ${theme.colors.neutral['200']};

  ${legacyTheme.breakpoints.up('md')} {
    display: grid;
    grid-template-areas: 'hero';
    min-height: calc(100vh - ${legacyTheme.props.headerHeight});
    background-color: transparent;
  }
`;

const Content = styled.div`
  display: flex;
  grid-area: hero;
  flex-flow: column nowrap;
  align-items: center;

  padding: ${legacyTheme.spacing.s48} ${legacyTheme.spacing.s24} ${legacyTheme.spacing.s64}
    ${legacyTheme.spacing.s24};

  ${legacyTheme.breakpoints.up('md')} {
    align-items: center;
    justify-content: center;
    padding: 0 ${legacyTheme.spacing.s24} 0 50%;
  }

  > div {
    max-width: 500px;
  }
`;

const Picture = styled.picture`
  ${legacyTheme.breakpoints.up('md')} {
    grid-area: hero;
    height: calc(100vh - ${legacyTheme.props.headerHeight});
    object-fit: cover;
  }
`;

const StyledImage = styled(Image)`
  width: 100%;
  height: 250px;
  object-fit: cover;

  ${legacyTheme.breakpoints.up('md')} {
    grid-area: hero;
    height: inherit;
    object-fit: cover;
  }
`;

const onFullListClickHandle = () => {
  document
    .getElementById('ingredients-full-list')
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

type GlossaryHeroProps = {
  content: {
    imageAlt: string;
    imageDesktop: StaticImageData;
    imageMobile: StaticImageData;
    subtitle: string;
    title: string | React.ReactNode;
  };
  dataFrom: string;
};

export const GlossaryHero = ({
  content: { imageAlt, imageDesktop, imageMobile, subtitle, title },
  dataFrom,
}: GlossaryHeroProps) => {
  const imagesCommonProps = { alt: imageAlt };
  const {
    props: { srcSet: desktopImgSrc },
  } = getImageProps({
    ...imagesCommonProps,
    width: 2888,
    height: 1426,
    src: imageDesktop.src,
  });
  const {
    props: { srcSet: mobileImgSrc, ...rest },
  } = getImageProps({
    ...imagesCommonProps,
    width: 750,
    height: 500,
    src: imageMobile.src,
  });

  return (
    <Section>
      <Picture>
        <source
          media={`(min-width: ${legacyTheme.breakpoints.values.md}px)`}
          srcSet={desktopImgSrc}
        />
        <StyledImage {...rest} alt={imagesCommonProps.alt} src={imageMobile} />
      </Picture>
      <Content>
        <div>
          <Typography paragraph variant="h1r">
            {title}
          </Typography>

          <Typography paragraph variant="p1">
            {subtitle}
          </Typography>
          <Button
            data-click="view-full-list"
            data-from={dataFrom}
            onClick={onFullListClickHandle}
            variant="underlined"
          >
            explore ingredients
          </Button>
        </div>
      </Content>
    </Section>
  );
};
